/**
 * Chart Theme Utility
 *
 * Provides theme-aware color configurations for ECharts.
 * Reads CSS custom properties at runtime so charts adapt to theme changes.
 *
 * Usage:
 *   const colors = getChartColors();
 *   const axis = getAxisConfig();
 */

/**
 * Get a CSS custom property value from the document root.
 * Falls back to the provided default if not found.
 */
function getCSSVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

/**
 * Chart color palette - reads from CSS variables.
 * Call this inside useMemo or component body to get current theme colors.
 */
export function getChartColors() {
  return {
    // Bandwidth
    rx: getCSSVar("--chart-rx", "#26AE7F"),
    tx: getCSSVar("--chart-tx", "#38bdf8"),

    // CPU series
    cpuUser: getCSSVar("--chart-cpu-user", "#60a5fa"),
    cpuSystem: getCSSVar("--chart-cpu-system", "#f59e0b"),
    cpuIowait: getCSSVar("--chart-cpu-iowait", "#f97316"),
    cpuNice: getCSSVar("--chart-cpu-nice", "#a78bfa"),
    cpuIrq: getCSSVar("--chart-cpu-irq", "#38bdf8"),
    cpuSoftirq: getCSSVar("--chart-cpu-softirq", "#22d3ee"),
    cpuSteal: getCSSVar("--chart-cpu-steal", "#f43f5e"),
    cpuGuest: getCSSVar("--chart-cpu-guest", "#34d399"),

    // Load average
    load1m: getCSSVar("--chart-load-1m", "#38bdf8"),
    load5m: getCSSVar("--chart-load-5m", "#a78bfa"),
    load15m: getCSSVar("--chart-load-15m", "#f97316"),

    // Memory
    memUsed: getCSSVar("--chart-mem-used", "#8b5cf6"),
    memCached: getCSSVar("--chart-mem-cached", "#38bdf8"),
    memBuffers: getCSSVar("--chart-mem-buffers", "#22d3ee"),
    memFree: getCSSVar("--chart-mem-free", "#34d399"),

    // Swap
    swapUsed: getCSSVar("--chart-swap-used", "#f97316"),
    swapFree: getCSSVar("--chart-swap-free", "#22d3ee"),
  };
}

/**
 * Get bandwidth chart colors as an array [rx, tx].
 */
export function getBandwidthColors(): [string, string] {
  return [
    getCSSVar("--chart-rx", "#26AE7F"),
    getCSSVar("--chart-tx", "#38bdf8"),
  ];
}

/**
 * Get CPU series configuration with theme-aware colors.
 */
export function getCpuSeriesConfig() {
  return [
    { key: "user", label: "User", color: getCSSVar("--chart-cpu-user", "#60a5fa") },
    { key: "system", label: "System", color: getCSSVar("--chart-cpu-system", "#f59e0b") },
    { key: "iowait", label: "I/O wait", color: getCSSVar("--chart-cpu-iowait", "#f97316") },
    { key: "nice", label: "Nice", color: getCSSVar("--chart-cpu-nice", "#a78bfa") },
    { key: "irq", label: "IRQ", color: getCSSVar("--chart-cpu-irq", "#38bdf8") },
    { key: "softirq", label: "SoftIRQ", color: getCSSVar("--chart-cpu-softirq", "#22d3ee") },
    { key: "steal", label: "Steal", color: getCSSVar("--chart-cpu-steal", "#f43f5e") },
    { key: "guest", label: "Guest", color: getCSSVar("--chart-cpu-guest", "#34d399") },
  ] as const;
}

/**
 * Get load average chart colors as an array [1m, 5m, 15m].
 */
export function getLoadColors(): [string, string, string] {
  return [
    getCSSVar("--chart-load-1m", "#38bdf8"),
    getCSSVar("--chart-load-5m", "#a78bfa"),
    getCSSVar("--chart-load-15m", "#f97316"),
  ];
}

/**
 * Get memory series configuration with theme-aware colors.
 */
export function getMemorySeriesConfig() {
  return [
    { key: "usedBytes", label: "Used", color: getCSSVar("--chart-mem-used", "#8b5cf6") },
    { key: "cachedBytes", label: "Cached", color: getCSSVar("--chart-mem-cached", "#38bdf8") },
    { key: "buffersBytes", label: "Buffers", color: getCSSVar("--chart-mem-buffers", "#22d3ee") },
    { key: "freeBytes", label: "Free", color: getCSSVar("--chart-mem-free", "#34d399") },
  ] as const;
}

/**
 * Get swap chart colors as an array [used, free].
 */
export function getSwapColors(): [string, string] {
  return [
    getCSSVar("--chart-swap-used", "#f97316"),
    getCSSVar("--chart-swap-free", "#22d3ee"),
  ];
}

/**
 * Get axis/grid styling configuration.
 * Uses CSS variables for theme-aware colors.
 */
export function getAxisConfig() {
  const axisRgb = getCSSVar("--chart-axis", "148 163 184");

  return {
    axisLine: {
      lineStyle: { color: `rgba(${axisRgb.replace(/ /g, ", ")}, 0.15)` },
    },
    axisTick: { show: false },
    axisLabel: {
      color: `rgba(${axisRgb.replace(/ /g, ", ")}, 0.7)`,
      fontSize: 10,
    },
    splitLine: {
      lineStyle: { color: `rgba(${axisRgb.replace(/ /g, ", ")}, 0.08)` },
    },
  };
}

/**
 * Get legend text style.
 */
export function getLegendStyle() {
  const axisRgb = getCSSVar("--chart-axis", "148 163 184");
  return {
    color: `rgba(${axisRgb.replace(/ /g, ", ")}, 0.9)`,
    fontSize: 10,
  };
}

/**
 * Get axis pointer color for tooltips.
 */
export function getAxisPointerColor(): string {
  const axisRgb = getCSSVar("--chart-axis", "148 163 184");
  return `rgba(${axisRgb.replace(/ /g, ", ")}, 0.4)`;
}

/**
 * Get CPU/Memory history chart colors (for CpuMemoryHistoryPanel).
 * CPU uses the system color, RAM uses the memory used color.
 */
export function getCpuMemoryHistoryColors() {
  const cpuColor = getCSSVar("--chart-cpu-system", "#f59e0b");
  const ramColor = getCSSVar("--chart-mem-used", "#8b5cf6");
  return {
    cpu: cpuColor,
    ram: ramColor,
    colors: [cpuColor, ramColor] as [string, string],
  };
}

/**
 * Get tooltip configuration with theme-aware colors.
 * @param primaryColor - Primary series color for border/axis pointer
 */
export function getTooltipColors(primaryColor: string) {
  // Convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return {
    borderColor: hexToRgba(primaryColor, 0.3),
    axisPointerColor: hexToRgba(primaryColor, 0.4),
  };
}

/**
 * Create a gradient color stop array for area charts.
 * @param color - Base color (hex)
 * @param topOpacity - Opacity at top (default 0.25)
 * @param bottomOpacity - Opacity at bottom (default 0.02)
 */
export function createAreaGradient(
  color: string,
  topOpacity = 0.25,
  bottomOpacity = 0.02
) {
  // Convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return {
    type: "linear" as const,
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: hexToRgba(color, topOpacity) },
      { offset: 1, color: hexToRgba(color, bottomOpacity) },
    ],
  };
}
